(function(root) {
  "use strict";
  const consent = root.LegitGrails.cookieConsent;
  const config = consent.config;
  let privacyApiPromise = null;
  let lastShopifyPayloadKey = null;
  let shopifyProjectionQueue = Promise.resolve();
  function warn() { if (root.console && root.console.warn) root.console.warn.apply(root.console, arguments); }
  function getPrivacyApi() {
    if (!root.Shopify) return null;
    return root.Shopify.customerPrivacy || null;
  }

  function waitForShopifyLoadFeatures(timeoutMs) {
    timeoutMs = timeoutMs || 4000;

    return new Promise(function(resolve) {
      let startedAt = Date.now();

      function tick() {
        if (root.Shopify && typeof root.Shopify.loadFeatures === "function") {
          resolve(root.Shopify.loadFeatures);
          return;
        }

        if (Date.now() - startedAt >= timeoutMs) {
          resolve(null);
          return;
        }

        root.setTimeout(tick, 50);
      }

      tick();
    });
  }

  /**
   * Loads Shopify Customer Privacy and reuses any in-flight request.
   *
   * @returns {Promise<object|null>} Customer Privacy API, when available.
   */
  function loadPrivacyApi() {
    if (getPrivacyApi()) return Promise.resolve(getPrivacyApi());
    if (privacyApiPromise) return privacyApiPromise;

    privacyApiPromise = waitForShopifyLoadFeatures(config.shopifyApiTimeoutMs || 4000)
      .then(function(loadFeatures) {
        if (getPrivacyApi()) return getPrivacyApi();
        if (!loadFeatures) return null;

        return new Promise(function(resolve) {
          let settled = false;
          const timeout = root.setTimeout(function() { finish(null); }, Number(config.shopifyApiTimeoutMs) || 4000);
          function finish(api) {
            if (settled) return;
            settled = true;
            root.clearTimeout(timeout);
            resolve(api);
          }
          try {
            loadFeatures([{ name: "consent-tracking-api", version: "0.1" }], function(error) {
              if (error) {
                warn("Shopify consent-tracking-api failed to load", error);
                finish(null);
                return;
              }

              finish(getPrivacyApi());
            });
          } catch (error) {
            warn("Shopify loadFeatures threw", error);
            finish(null);
          }
        });
      })
      .then(function(api) {
        // Cache only an in-flight load. A failed/timed-out request must not
        // permanently prevent a later loadFeatures attempt from succeeding.
        privacyApiPromise = null;
        return api;
      }, function(error) {
        privacyApiPromise = null;
        throw error;
      });

    return privacyApiPromise;
  }

  function stringifyPayload(payload) {
    let normalized = {
      preferences: Boolean(payload.preferences),
      analytics: Boolean(payload.analytics),
      marketing: Boolean(payload.marketing)
    };

    if (typeof payload.sale_of_data === "boolean") {
      normalized.sale_of_data = payload.sale_of_data;
    }

    return JSON.stringify(normalized);
  }

  function callSetTrackingConsent(api, payload) {
    if (!api || typeof api.setTrackingConsent !== "function") {
      return Promise.resolve(false);
    }

    return new Promise(function(resolve) {
      let settled = false;
      let timeout = root.setTimeout(function() {
        warn("Shopify setTrackingConsent callback timed out");
        done(false);
      }, Number(config.shopifyConsentTimeoutMs) || 4000);

      function done(result) {
        if (settled) return;
        settled = true;
        root.clearTimeout(timeout);

        if (result && result.error) {
          warn("Shopify setTrackingConsent failed", result.error);
          resolve(false);
          return;
        }

        resolve(result !== false);
      }

      try {
        let result = api.setTrackingConsent(payload, done);

        if (result && typeof result.then === "function") {
          result.then(done).catch(function(error) {
            warn("Shopify setTrackingConsent rejected", error);
            done(false);
          });
        }
      } catch (error) {
        warn("Shopify setTrackingConsent threw", error);
        done(false);
      }
    });
  }

  /**
   * Projects the local consent state into Shopify Customer Privacy enforcement.
   *
   * Calls are serialized because Shopify writes `_tracking_consent`; identical
   * payloads are skipped once the previous projection is known.
   *
   * @param {{preferences: boolean, analytics: boolean, marketing: boolean, sale_of_data?: boolean}} payload
   * @param {number} [revision] Store decision revision.
   * @returns {Promise<boolean>} Whether Shopify acknowledged the projection.
   */
  function setShopifyTrackingConsent(payload, revision) {
    const payloadKey = stringifyPayload(payload);
    const decisionRevision = revision === undefined ? consent.getState().decisionRevision : revision;

    shopifyProjectionQueue = shopifyProjectionQueue
      .catch(function() {})
      .then(function() {
        // A queued old choice must not overwrite a newer local decision.
        if (consent.getState().decisionRevision !== decisionRevision) return false;
        if (payloadKey === lastShopifyPayloadKey) {
          consent.markShopifySynced(decisionRevision);
          return true;
        }
        return loadPrivacyApi().then(function(api) {
          if (consent.getState().decisionRevision !== decisionRevision) return false;
          if (!api) {
            warn("Shopify privacy API unavailable; keeping decision pending");
            return false;
          }
          return callSetTrackingConsent(api, payload).then(function(result) {
            if (result && consent.getState().decisionRevision === decisionRevision) {
              lastShopifyPayloadKey = payloadKey;
              consent.markShopifySynced(decisionRevision);
            } else {
              // An in-flight stale write may have changed Shopify, while Back
              // may have changed it elsewhere. Neither may seed current dedupe.
              lastShopifyPayloadKey = null;
            }
            return result;
          });
        });
      });

    shopifyProjectionQueue = shopifyProjectionQueue.catch(function(error) {
      lastShopifyPayloadKey = null;
      warn("Shopify projection failed; keeping decision pending", error);
      return false;
    });
    return shopifyProjectionQueue;
  }

  /**
   * Maps the theme's effective consent categories to Shopify's tracking fields.
   *
   * @param {object} state Consent store snapshot.
   * @returns {object} Shopify tracking-consent payload.
   */
  function buildShopifyPayload(state) {
    let effective = state && state.effectiveCategories
      ? state.effectiveCategories
      : consent.getEffectiveCategories();
    let isUsaCa = state && state.region === "USA_CA";
    let hasGpc = Boolean(root.navigator && root.navigator.globalPrivacyControl === true);
    let payload = {
      preferences: Boolean(effective.functional),
      analytics: Boolean(effective.analytics),
      marketing: Boolean(effective.advertisement)
    };

    if (isUsaCa && !hasGpc && typeof state.saleOfData === "boolean") {
      payload.sale_of_data = state.saleOfData;
    }

    return payload;
  }


  /**
   * One region lookup operation: wait for SDK readiness, then for a usable region.
   * The operation timeout is an error, not a separate EU-banner display timer.
   * @returns {Promise<ConsentRegionContext>}
   */
  function resolveRegion() {
    return loadPrivacyApi().then(function(api) {
      if (!api) throw new Error("Shopify privacy API unavailable");
      return new Promise(function(resolve, reject) {
        let poll = null;
        let settled = false;
        const timeout = root.setTimeout(function() { finish(null); }, Number(config.shopifyApiTimeoutMs) || 4000);
        function finish(context) {
          if (settled) return;
          settled = true;
          root.clearTimeout(timeout);
          root.clearTimeout(poll);
          if (context) resolve(context);
          else reject(new Error("Shopify region unavailable"));
        }
        function tick() {
          const context = consent.region.resolveFromShopify(config, api);
          if (context.source === "SHOPIFY") { finish(context); return; }
          poll = root.setTimeout(tick, 50);
        }
        tick();
      });
    });
  }
  function syncPending() {
    const state = consent.getState();
    if (!state.isRegionResolved || !state.hasExplicitConsent || !state.needsSync) return Promise.resolve(false);
    return setShopifyTrackingConsent(buildShopifyPayload(state), state.decisionRevision);
  }
  consent.shopify = { loadPrivacyApi: loadPrivacyApi, resolveRegion: resolveRegion,
    buildPayload: buildShopifyPayload, project: setShopifyTrackingConsent, syncPending: syncPending,
    resetProjectionCache: function() { lastShopifyPayloadKey = null; } };
})(window);
